import tokens from '@kluein/design-tokens';
import { Box, Skeleton, Text, TextInput } from '@kluein/klue-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAskKlueLog } from 'hooks/dashboard/use-ask-klue-log';
import { AnalyticsAction } from 'lib/analytics';
import {
  useAskKlue,
  useAskKlueChatSessions,
  useAskKlueRestoreSession,
} from 'pages/dashboard/hooks';
import { sidebarWidth } from 'pages/profile/views/battlecards/utils';
import {
  AskChatSessionCategory,
  type AskKlueChatSessionProps,
} from 'store/api/ask-klue/chat-sessions';
import { setAskKlueInitialScreen } from 'store/views';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import { SidebarItem } from './partials/sidebar-item';

import type { AskKlueModalSidebarProps } from './AskKlueModalSidebar.types';

export const StyledTextInput = styled(TextInput)`
  &::placeholder {
    width: 100%;
  }
`;

function TopicTitle({
  title,
  showDivider = false,
}: {
  title: string;
  showDivider?: boolean;
}) {
  return (
    <Box>
      <Text
        fontFamily={tokens.fontFamily.Inter}
        size={tokens.fontSize.bodyFont.medium}
        fontWeight="medium"
        color={tokens.color.primitives.gray[425]}
      >
        {title}
      </Text>
      {showDivider && (
        <Box
          width="full"
          height={tokens.spacing.hair}
          background={tokens.color.neutral[500]}
          margin={{ top: 'xsmall', bottom: 'small' }}
        />
      )}
    </Box>
  );
}

function SideBarContent(props: AskKlueModalSidebarProps) {
  const { chatSessions, isLoading, isError } = props;
  const { t } = useTranslation(['Dashboard']);
  const { setRestoreSession, restoreSessionId } = useAskKlueRestoreSession();
  const { askKlueQueryParams } = useAskKlue();
  const logAskKlueEvent = useAskKlueLog();

  const handleClick = (item: AskKlueChatSessionProps) => {
    if (
      restoreSessionId === item.session_id &&
      askKlueQueryParams?.askKlueSourceApi === item.source_api
    ) {
      return;
    }
    setAskKlueInitialScreen(false);
    setRestoreSession(item.session_id, item.category, item.source_api);
    logAskKlueEvent({
      askKlueId: item.session_id,
      eventType: AnalyticsAction.clickChatSessionItem,
      text: item.text,
    });
  };

  const padding = {
    left: tokens.spacing.large,
    right: tokens.spacing.medium,
  };

  if (isError) {
    return (
      <Box
        width={sidebarWidth}
        pad={{
          ...padding,
          top: tokens.spacing.medium,
        }}
      >
        <Box>
          <Text
            fontWeight="medium"
            lineHeight="sparse"
            color={tokens.color.priority.critical[800]}
          >
            {t('Dashboard:askKlue.error.networkErrorTitle')}
          </Text>
          <Text
            fontWeight="medium"
            lineHeight="sparse"
            color={tokens.color.neutral.black.main}
          >
            {t('Dashboard:askKlue.error.networkGenericError')}
          </Text>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        pad={{ ...padding, top: 'small' }}
        data-test-id={TEST_IDS.askKlue.sidebar.loading}
      >
        <Skeleton height={'500px'} animated={true} />
      </Box>
    );
  }

  return (
    <Box pad={{ top: 'xsmall', bottom: 'xsmall' }}>
      {chatSessions?.map((group) => (
        <Box flex={{ grow: 1 }} key={group.id}>
          <Box pad={{ ...padding, top: 'small' }} flex={{ grow: 1 }}>
            <TopicTitle title={group.name} showDivider />
          </Box>
          {group.items?.map((item, index) => (
            <SidebarItem
              key={`${item.session_id}-${item.source_api}`}
              isActive={
                restoreSessionId === item.session_id &&
                askKlueQueryParams?.askKlueSourceApi === item.source_api
              }
              handleClick={() => handleClick(item)}
              index={index}
              title={item.text}
              sessionId={item.session_id}
              isRM={item.category === AskChatSessionCategory.RESEARCH}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function AskKlueModalSidebar() {
  const { t } = useTranslation();
  const { chatSessions, searchValue, setSearchValue, isLoading, isError } =
    useAskKlueChatSessions();

  const padding = {
    left: tokens.spacing.large,
    right: tokens.spacing.medium,
  };

  return (
    <Box
      width={sidebarWidth}
      style={{
        maxWidth: sidebarWidth,
      }}
      flex={{ grow: 1 }}
      data-test-id={TEST_IDS.askKlue.sidebar.container}
      pad={{
        bottom: tokens.spacing.medium,
      }}
      overflow="auto"
    >
      <Box
        pad={{
          ...padding,
          top: tokens.spacing.medium,
        }}
        flex={false}
        style={{ position: 'sticky', top: 0, zIndex: 1 }}
        background={tokens.color.neutral[100]}
      >
        <TopicTitle title={t('Dashboard:askKlue.answerModal.sidebar.title')} />
        <Box pad={{ top: 'xsmall' }}>
          <StyledTextInput
            data-test-id={TEST_IDS.askKlue.sidebar.searchInput}
            data-tracking-id={TRACKING_IDS.askKlue.sidebar.searchInput}
            type="search"
            value={searchValue}
            placeholder={t('Dashboard:askKlue.answerModal.sidebar.placeholder')}
            title={t('Dashboard:askKlue.answerModal.sidebar.placeholder')}
            size="medium"
            aria-label={t('Dashboard:askKlue.answerModal.sidebar.placeholder')}
            onChange={(event) => setSearchValue(event.target.value)}
            variant="search"
          />
        </Box>
      </Box>
      <SideBarContent
        chatSessions={chatSessions}
        isLoading={isLoading}
        isError={isError}
      />
    </Box>
  );
}

export default AskKlueModalSidebar;
